import _ from 'lodash';
import Vue from 'vue';
import VueApollo from 'vue-apollo';
import { ApolloClient } from 'apollo-client';
import { HttpLink } from 'apollo-link-http';
import { ApolloLink, from, Observable } from 'apollo-link';
import { onError } from 'apollo-link-error';
import { InMemoryCache, defaultDataIdFromObject } from 'apollo-cache-inmemory';
import { TokenRefreshLink } from "apollo-link-token-refresh";
// import { parse } from 'graphql'

import jwtDecode from 'jwt-decode';
import { fromUnixTime, differenceInMinutes, isPast } from 'date-fns';

import { isTokenValidOrUndefined, fetchAccessToken, isRefreshInFlight, waitForRefreshInFlight } from '@/helpers';

import Auth from '@/store/api/system/auth';
import pinia from '@/pinia';
import { useAuthStore, useSetupStore } from '@/stores';

// Define GraphQL endpoints
const ENDPOINT = `${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_GRAPHQL_ROUTE}`;
const defaultHttpLink = new HttpLink({ uri: `${ENDPOINT}` });
const adminHttpLink = new HttpLink({ uri: `${ENDPOINT}admin` });

// ======================
// Custom Active Query Monitor
// ======================
// This variable is used to keep track of the number of active (in-flight) queries.
let activeQueries = 0;

// Apollo Link that increments activeQueries on request start and decrements it on completion.
const activeQueryMonitor = new ApolloLink((operation, forward) => {
    activeQueries++;
    return forward(operation).map(result => {
        activeQueries--;
        return result;
    });
});

// ======================
// Apollo Links and Middlewares
// ======================

const refreshTokenMiddleware = new TokenRefreshLink({
    accessTokenField: 'token',
    isTokenValidOrUndefined,
    fetchAccessToken,
    handleResponse: (operation, accessTokenField) => response => {
        return { token: response };
    },
    handleError: (error, operation) => {
        const status = _.get(error, 'response.status');
        switch (status) {
            case 503: {
                const message = _.get(error, 'response.data.reponse');
                useSetupStore(pinia).updateMaintenanceState(message || true);
                break;
            }
            case 404:
                // TODO: handle 404 accordingly
                break;
            case 401:
            case 403:
            case 422:
                useAuthStore(pinia).signout();
                break;
        }
    },
});

const authMiddleware = new ApolloLink((operation, forward) => {
    // ONE token. Entering a customer's account swaps it for the session's;
    // leaving swaps it back. Nothing here has to decide which of two tokens
    // a request should carry - that decision, guessed per call site, is how
    // a refreshed token once landed in the wrong slot and silently dropped a
    // support session back into our own account mid-session.
    const token = Auth.getToken();

    operation.setContext({
        headers: { authorization: token ? `Bearer ${token}` : null }
    });

    return forward(operation).map(response => {
        const context = operation.getContext();
        const {
            response: { headers }
        } = context;

        if (headers) {
            const refreshed = headers.get('Authorization');
            if (refreshed) {
                const jwt = refreshed.split('Bearer');
                if (jwt.length) {
                    useAuthStore(pinia).addTokenToState({ token: _.compact(jwt)[0].trim() });
                }
            }
        }
        return response;
    });
});

const networkErrorLink = onError(({ networkError, graphQLErrors, operation, forward }) => {
    if (networkError) {
        switch (networkError.statusCode) {
            case 503: {
                const message = _.get(networkError, 'result.message');
                useSetupStore(pinia).updateMaintenanceState(message || true);
                break;
            }
            case 404:
                // TODO: handle 404 errors if needed
                break;
            // The support session this token belongs to was ended elsewhere -
            // stopped in another tab, or revoked by the platform team. The
            // answer carries the way home: a token for our own account. Swap
            // it in and carry on as ourselves; never the sign-out below.
            case 409:
                if (_.get(networkError, 'result.response') === 'impersonation-ended') {
                    useAuthStore(pinia).sessionEndedElsewhere(networkError.result);
                }
                break;
            case 401:
            case 403:
            case 422:
                // If a token refresh is already in-flight, wait for it and retry
                if (isRefreshInFlight()) {
                    return new Observable(function (observer) {
                        waitForRefreshInFlight()
                            .then(function () {
                                var subscriber = {
                                    next: observer.next.bind(observer),
                                    error: observer.error.bind(observer),
                                    complete: observer.complete.bind(observer)
                                };
                                forward(operation).subscribe(subscriber);
                            })
                            .catch(function () {
                                useAuthStore(pinia).signout();
                            });
                    });
                }
                useAuthStore(pinia).signout();
                break;
        }
    } else if (graphQLErrors) {
        const retryOnGraphQLError = operation.getContext().retryOnGraphQLError || false;
        if (retryOnGraphQLError) {
            return forward(operation);
        } else {
            return;
        }
    }
    return forward(operation);
});

// ======================
// Apollo Client Instances
// ======================

// Note: The activeQueryMonitor is integrated only into the default client's link chain.
export const defaultClient = new ApolloClient({
    link: from([
        refreshTokenMiddleware,
        authMiddleware,
        networkErrorLink,
        activeQueryMonitor,
        defaultHttpLink,
    ]),
    cache: new InMemoryCache({
        dataIdFromObject: object => {
            let id = defaultDataIdFromObject(object);
            switch (object.__typename) {
                case 'CO_ArticleStock':
                case 'CO_PurchasedItemStock':
                    if (object.pivot && object.pivot.id) {
                        id = `${object.id}:${object.pivot.id}`;
                    }
                    break;
                case 'CO_PurchasedItemCancellation':
                    // Cancellation.items returns CO_PurchasedItemCancellation
                    // whose `id` is the PurchasedItem id — shared across all
                    // cancellations of the same item. Each row has its own
                    // pivot (qty/credited_amount/reason/comment). Without a
                    // composite key Apollo collapses the entries and the
                    // pivot of one cancellation leaks into the rendering of
                    // sibling cancellations covering the same item.
                    if (object.pivot && object.pivot.id) {
                        id = `${object.id}:${object.pivot.id}`;
                    }
                    break;
                case 'ImageVariant':
                    // The synthesized "original" layer has the CONSTANT id "original",
                    // so every image's variant normalizes to the same cache entry
                    // (ImageVariant:original) and all previews collapse onto whichever
                    // image was written last. Variants are always fetched inside their
                    // parent Image (never standalone), so store them inline (no id).
                    id = null;
                    break;
            }
            return id;
        }
    }),
    connectToDevTools: process.env.NODE_ENV === 'development',
});

export const adminClient = new ApolloClient({
    link: from([
        refreshTokenMiddleware,
        authMiddleware,
        networkErrorLink,
        activeQueryMonitor,
        adminHttpLink,
    ]),
    cache: new InMemoryCache({
        dataIdFromObject: object => {
            switch (object.__typename) {
                case 'CO_ArticleStock':
                case 'CO_PurchasedItemStock':
                    if (object.pivot && object.pivot.id) {
                        return object.id + ':' + object.pivot.id;
                    }
                    return defaultDataIdFromObject(object);
                case 'CO_PurchasedItemCancellation':
                    // Same fix as defaultClient — Cancellation.items returns
                    // CO_PurchasedItemCancellation with the PurchasedItem id,
                    // so two cancellations covering the same item collide
                    // unless we key by item:pivot composite.
                    if (object.pivot && object.pivot.id) {
                        return object.id + ':' + object.pivot.id;
                    }
                    return defaultDataIdFromObject(object);
                case 'UserSettingsDashboardLayoutItem':
                case 'UserSettingsDashboardLayout':
                case 'UserSettingsDashboardStandaloneWidget':
                    return null;
                case 'ImageVariant':
                    // The synthesized "original" layer has the CONSTANT id "original",
                    // so every image's variant collapses to one cache entry
                    // (ImageVariant:original) and previews show the last-written image
                    // (the menuItem/page/slider "wrong preview" bug). Variants are always
                    // fetched inside their parent Image, so store them inline (no id).
                    return null;
                default:
                    return defaultDataIdFromObject(object);
            }
        }
    }),
    connectToDevTools: process.env.NODE_ENV === 'development',
});

const apolloProvider = new VueApollo({
    clients: {
        default: defaultClient,
        admin: adminClient,
    },
    defaultClient,
});

Vue.use(VueApollo);

// ======================
// Safe Clear Store Function
// ======================
// This function waits until all active queries have finished before clearing the Apollo cache.
export async function safeClearStore(client = defaultClient) {
    while (activeQueries > 0) {
        await new Promise(resolve => setTimeout(resolve, 50));
    }
    await client.clearStore();
}

export default apolloProvider;
